import { Button, Group, Input, Stack, Text, ThemeIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconKey, IconLock } from '@tabler/icons-react';
import { CopyToClipboard } from 'components/CopyToClipboard';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';

import { useLoadApiCredentials, useRegenerateApiSecret } from '../api';

import { SecretRevealModal } from './SecretRevealModal';

export const ApiCard = () => {
  const { t } = useTranslation();
  const { apiCredentials, isApiCredentialsLoading } = useLoadApiCredentials();
  const { regenerateApiSecret, isRegenerateApiSecretLoading } = useRegenerateApiSecret();

  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [isRevealOpen, { open: openReveal, close: closeReveal }] = useDisclosure(false);

  const confirmReset = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.SETTINGS.COMPANY.RESET_SECRET_ID),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.SETTINGS.COMPANY.AYS_RESET_SECRET_ID)}</Text>,
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      onConfirm: () =>
        regenerateApiSecret(undefined, {
          onSuccess: response => {
            setRevealedSecret(response.data.secret_id);
            openReveal();
          },
        }),
    });

  const clientId = apiCredentials?.clientId;

  return (
    <Panel
      title={t(i18nKeys.SETTINGS.COMPANY.API_TITLE)}
      withBorder
      fullHeight
      isLoading={isApiCredentialsLoading}
    >
      {apiCredentials?.usePublicAPI ? (
        <Stack gap="md" my="md">
          <Input.Wrapper label={t(i18nKeys.SETTINGS.CLIENT_ID)}>
            <Group
              gap="xs"
              wrap="nowrap"
              px="sm"
              py={6}
              bg="var(--mantine-color-gray-light)"
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-sm)',
              }}
            >
              <ThemeIcon variant="transparent" color="gray" size="sm">
                <IconLock size={16} />
              </ThemeIcon>
              <Text size="sm" fw={500} style={{ flexGrow: 1, wordBreak: 'break-all' }}>
                {clientId ?? ''}
              </Text>
              {clientId != null && <CopyToClipboard text={clientId} />}
            </Group>
          </Input.Wrapper>
          <Group>
            <Button
              variant="light"
              leftSection={<IconKey size={16} />}
              onClick={confirmReset}
              loading={isRegenerateApiSecretLoading}
            >
              {t(i18nKeys.SETTINGS.COMPANY.RESET_SECRET_ID)}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed" my="md">
          {t(i18nKeys.SETTINGS.COMPANY.API_NOT_ENABLED)}
        </Text>
      )}
      <SecretRevealModal
        opened={isRevealOpen}
        onClose={closeReveal}
        clientId={clientId ?? null}
        secret={revealedSecret}
      />
    </Panel>
  );
};
